/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { riskExclusionsSuggest } from "../funcs/riskExclusionsSuggest.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SuggestExclusionResult } from "../models/components/suggestexclusionresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SuggestExclusionRequest,
  SuggestExclusionSecurity,
} from "../models/operations/suggestexclusion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RiskSuggestExclusionMutationVariables = {
  request: SuggestExclusionRequest;
  security?: SuggestExclusionSecurity | undefined;
  options?: RequestOptions;
};

export type RiskSuggestExclusionMutationData = SuggestExclusionResult;

export type RiskSuggestExclusionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * suggestExclusion risk
 *
 * @remarks
 * Suggest a risk exclusion (match_type, match_value, filters) from a natural-language prompt, a batch of example findings, or both. Calls the configured LLM with a JSON-schema constrained response so the dashboard can prefill the create exclusion form. At least one of prompt or finding_ids is required.
 */
export function useRiskSuggestExclusionMutation(
  options?: MutationHookOptions<
    RiskSuggestExclusionMutationData,
    RiskSuggestExclusionMutationError,
    RiskSuggestExclusionMutationVariables
  >,
): UseMutationResult<
  RiskSuggestExclusionMutationData,
  RiskSuggestExclusionMutationError,
  RiskSuggestExclusionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRiskSuggestExclusionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRiskSuggestExclusion(): MutationKey {
  return ["@gram/client", "exclusions", "suggest"];
}

export function buildRiskSuggestExclusionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RiskSuggestExclusionMutationVariables,
  ) => Promise<RiskSuggestExclusionMutationData>;
} {
  return {
    mutationKey: mutationKeyRiskSuggestExclusion(),
    mutationFn: function riskSuggestExclusionMutationFn({
      request,
      security,
      options,
    }): Promise<RiskSuggestExclusionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(riskExclusionsSuggest(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
